// src/components/models/FluxTwoProControlPanel.tsx
import React, { useCallback } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import { FluxTwoProParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

type FluxTwoProResolution = FluxTwoProParams['resolution'];

const RESOLUTION_OPTIONS: FluxTwoProResolution[] = [
  'match_input_image',
  '0.5 MP',
  '1 MP',
  '2 MP',
  '4 MP',
];

interface FluxTwoProControlPanelProps {
  params: FluxTwoProParams;
  setModelParams: (params: (prevParams: { [model: string]: FluxTwoProParams }) => { [model: string]: FluxTwoProParams }) => void;
  diffusionModel: string;
}

// Référence stable : AutoGrowingTextarea mémoïse son style à partir de cette prop.
const NO_TEXTAREA_STYLE_OVERRIDES = {};

const defaultFluxTwoProParams: FluxTwoProParams = {
  prompt: '',
  resolution: '1 MP',
  seed: -1,
};

export const getDefaultFluxTwoProParams = (): FluxTwoProParams => ({ ...defaultFluxTwoProParams });

const FluxTwoProControlPanel: React.FC<FluxTwoProControlPanelProps> = React.memo(({
  params, setModelParams, diffusionModel
}) => {
  const { kernel } = useKernel();

  const updateParam = useCallback(<K extends keyof FluxTwoProParams>(name: K, value: FluxTwoProParams[K]) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[FluxTwoProControlPanel] updateParam: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        }
      };
    });

    if (name === 'prompt') {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: name,
        value_length: (value as string).length,
        model_name: diffusionModel,
        source_component: 'FluxTwoProControlPanel'
      });
    } else {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: name,
        value,
        model_name: diffusionModel,
        source_component: 'FluxTwoProControlPanel'
      });
    }
  }, [setModelParams, diffusionModel, kernel]);

  const handleParamChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    updateParam(name as keyof FluxTwoProParams, value);
  }, [updateParam]);

  const handleResolutionChange = useCallback((value: string) => {
    updateParam('resolution', value as FluxTwoProResolution);
  }, [updateParam]);

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe what you want to change..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-white mb-1">Resolution</label>
        <Select
          id="flux2-resolution"
          value={params.resolution}
          options={RESOLUTION_OPTIONS}
          onChange={handleResolutionChange}
        />
      </div>
      <p className="text-xs text-[#4D4D4D]">
        Up to 8 reference images (including red-mask renders) are supported via the Reference Images uploader.
      </p>

    </>
  );
});
FluxTwoProControlPanel.displayName = 'FluxTwoProControlPanel';

export default FluxTwoProControlPanel;

